import type {
  AgentConfig,
  ChannelSettings,
  AgentStreamData,
  SharedAllowedAttributes,
} from 'src/types/agent-builder';
import type {Dispatch, ThunkAction} from 'src/reducers';

import * as reduxApi from 'src/utils/redux-api-v2';


export const RECEIVE_CHANNEL_SETTINGS =
  'agent-builder/receive_channel_settings';
export const RECEIVE_AGENT_SUGGESTION =
  'agent-builder/receive_agent_suggestion';
export const RECEIVE_AGENT_STREAM_DATA =
  'agent-builder/receive_agent_stream_data';
export const RECEIVE_ALLOWED_ATTRIBUTES =
  'agent-builder/receive_allowed_attributes';
export const RECEIVE_AGENT_CONFIGURATION =
  'agent-builder/receive_agent_configuration';
export const RECEIVE_AGENT_BUILDING = 'agent-builder/receive_agent_building';

export interface ReceiveChannelSettingsAction {
  type: typeof RECEIVE_CHANNEL_SETTINGS;
  payload: ChannelSettings;
}

export interface ReceiveAgentSuggestionAction {
  type: typeof RECEIVE_AGENT_SUGGESTION;
  payload: string | null;
}

export interface ReceiveAllowedAttributesAction {
  type: typeof RECEIVE_ALLOWED_ATTRIBUTES;
  payload: SharedAllowedAttributes;
}

export interface ReceiveAgentStreamDataAction {
  type: typeof RECEIVE_AGENT_STREAM_DATA;
  payload: Partial<AgentStreamData>;
}

export interface ReceiveAgentConfigurationAction {
  type: typeof RECEIVE_AGENT_CONFIGURATION;
  payload: AgentConfig | null;
}

export interface ReceiveAgentBuildingAction {
  type: typeof RECEIVE_AGENT_BUILDING;
  payload: boolean;
}

export type AgentBuilderActions =
  | ReceiveChannelSettingsAction
  | ReceiveAgentSuggestionAction
  | ReceiveAgentStreamDataAction
  | ReceiveAllowedAttributesAction
  | ReceiveAgentConfigurationAction
  | ReceiveAgentBuildingAction;

export const receiveChannelSettings = (
  payload: ChannelSettings,
): ReceiveChannelSettingsAction => ({
  type: RECEIVE_CHANNEL_SETTINGS,
  payload,
});

export const receiveAgentSuggestion = (
  payload: string | null,
): ReceiveAgentSuggestionAction => ({
  type: RECEIVE_AGENT_SUGGESTION,
  payload,
});

export const receiveAllowedAttributes = (
  payload: SharedAllowedAttributes,
): ReceiveAllowedAttributesAction => ({
  type: RECEIVE_ALLOWED_ATTRIBUTES,
  payload,
});

export const receiveAgentConfiguration = (
  payload: AgentConfig,
): ReceiveAgentConfigurationAction => ({
  type: RECEIVE_AGENT_CONFIGURATION,
  payload,
});

export const receiveAgentStreamData = (
  payload: Partial<AgentStreamData>,
): ReceiveAgentStreamDataAction => ({
  type: RECEIVE_AGENT_STREAM_DATA,
  payload,
});

export const receiveAgentBuilding = (
  payload: boolean,
): ReceiveAgentBuildingAction => ({
  type: RECEIVE_AGENT_BUILDING,
  payload,
});

export const getChannelSettings =
  (agentID: string): ThunkAction<ChannelSettings> =>
  async (dispatch: Dispatch): Promise<ChannelSettings> => {
    try {
      const response = await dispatch<ChannelSettings>(
        reduxApi.get(`agent-builder/agents/${agentID}/channel_settings`),
      );
      return dispatch(receiveChannelSettings(response));
    } catch (error: any) {
      console.error(error);
      throw new Error('Channel settings error.');
    }
  };

export const getAllowedAttributes =
  (payload: {
    anchor_entity_type: string;
    recipient_entity_type: string;
  }): ThunkAction<SharedAllowedAttributes> =>
  async (dispatch: Dispatch): Promise<SharedAllowedAttributes> => {
    try {
      const response = await dispatch<SharedAllowedAttributes>(
        reduxApi.post('shared/allowed-attributes', payload),
      );
      return dispatch(receiveAllowedAttributes(response));
    } catch (error: any) {
      console.error(error);
      throw new Error('Allowed attributes error.');
    }
  };

export const getAgentConfiguration =
  (agentID: string): ThunkAction<AgentConfig> =>
  async (dispatch: Dispatch): Promise<AgentConfig> => {
    try {
      const response = await dispatch<AgentConfig>(
        reduxApi.get(`agent-builder/agents/${agentID}/config`),
      );
      return dispatch(receiveAgentConfiguration(response));
    } catch (error: any) {
      console.error(error);
      throw new Error('Agent configuration error.');
    }
  };
